/* eslint-disable no-nested-ternary */
/* eslint-disable no-await-in-loop */
import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense } from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box, Button, Card, MenuItem, Select,
    Stack, TextField, Typography, IconButton
} from "@mui/material";
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc, where, writeBatch } from "firebase/firestore";
import Papa from "papaparse";
import { db } from "../../../../../firebase.config";
import Iconify from "../../../../../components/Iconify";
import LucideIcon from "../../../../../components/LucideIcon";
import CustomButton from "../../../../../components/CustomButton";
import ImportContactTemplateButton from "../../../../../components/downloadTemplate";
import PopulationFilters from "../../../../../components/filters/contactFilters";
import useApproxContactsCount from "../../../../../components/filters/useApproxContactsCount";
import { Variables } from "../../../../../context/VariableContext";
import { FilterChipsSummary } from "./filterChipsSummary";

// Lazy load de l’emoji picker (gagne ~200-300kb au premier rendu)
const EmojiPicker = React.lazy(() => import("emoji-picker-react"));

/** Utilitaires */
const endpointCreatePop = "https://us-central1-event-86cbf.cloudfunctions.net/createDynamicPopulation";
const endpointApproxCount = "https://us-central1-event-86cbf.cloudfunctions.net/contactApproxCountv2";
// Optionnel (recommandé pour CSV) : implémente une CF serveur pour importer en BulkWriter
const endpointImportCSV = "https://us-central1-event-86cbf.cloudfunctions.net/importContactsToPopulation"; // à créer côté serveur

const DEFAULT_MAPPINGS = { email: "email", telephone: "telephone", nom: "nom", prenoms: "prenoms", abonne: "abonne" };
const BATCH_LIMIT = 500;
const nf = (n) => new Intl.NumberFormat("fr-FR").format(n ?? 0);

export default function CreatePopulation({ orgID, populationID }) {
    const { openDrawer, setOpenDrawer } = Variables();
    /** ÉTATS PRINCIPAUX */
    const [expanded, setExpanded] = useState(false);
    const [filters, setFilters] = useState({
        filterEvent: [],
        filterInterest: [],
        filterNbEvent: { min: "", max: "" },
        filterBillet: { min: "", max: "" },
        totalDepense: { min: "", max: "" },
        lastDateOrder: { before: null, after: null },
        filterNewsletter: null,
        filterNotif: null,
        filterAbonne: null,
        filterHist: null,
        start: null,
        end: null
    });

    const [events, setEvents] = useState([]);
    const [types, setTypes] = useState([]);
    const [step, setStep] = useState(0);
    const [type, setType] = useState(0); // 0 dynamique, 1 manuelle
    const [nom, setNom] = useState("");
    const [desc, setDesc] = useState("");
    const [loading, setLoading] = useState(false);

    // Emoji
    const [chosenEmoji, setChosenEmoji] = useState("😊");
    const [showPicker, setShowPicker] = useState(false);

    // CSV
    const [fileName, setFileName] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [fieldMapping, setFieldMapping] = useState({});
    const csvAbortRef = useRef(null);

    const isEdit = !!populationID;
    const isDynamicEdit = isEdit && type === 0;
    const isManualEdit = isEdit && type === 1;

    /** ----- Effets init ----- */
    useEffect(() => {
        if (!orgID) return;
        (async () => {
            try {
                setLoading(true);
                // Charger la liste des types (une fois)
                const qTypes = query(collection(db, `types`));
                const snap = await getDocs(qTypes);
                const list = [];
                snap.forEach((d) => list.push(d));
                setTypes(list);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [orgID]);

    // Si édition : charge la population existante
    React.useEffect(() => {
        if (!populationID || !orgID) return;
        (async () => {
            try {
                setLoading(true);
                const popRef = doc(db, `organisations/${orgID}/crm/${orgID}/populations`, populationID);
                const snap = await getDoc(popRef);
                if (snap.exists()) {
                    const data = snap.data();
                    setType(data?.type ?? 0);
                    setNom(data?.name ?? "");
                    setDesc(data?.description ?? "");
                    setChosenEmoji(data?.emoji ?? "😊");
                    if (data?.type === 0) {
                        // dynamique : on peut charger les filtres pour affichage en "readonly" si besoin
                        setFilters(data?.filters ?? filters);
                        setStep(2); // on va DIRECTEMENT à l'écran Nom/Desc/Emoji
                    } else {
                        // manuel : on commence à l'import CSV
                        setStep(1);
                    }
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [populationID, orgID]);


    /** ----- Handlers mémoïsés ----- */
    const onChangeFilters = useCallback((next) => setFilters(next), []);
    const onTogglePicker = useCallback(() => setShowPicker((v) => !v), []);
    const onEmojiClick = useCallback((emojiObject) => {
        setChosenEmoji(emojiObject.emoji);
        setShowPicker(false);
    }, []);

    const handleExpandPanel1 = useCallback(async (_evt, isExpanded) => {
        setExpanded(isExpanded ? "panel1" : false);
        if (isExpanded && events.length === 0 && orgID) {
            try {
                setLoading(true);
                // Pas besoin d’aller chercher organisateurs/{uid}, tu as déjà orgID
                const qEvts = query(
                    collection(db, "evenement"),
                    where("orgId", "==", orgID),
                    orderBy("dateDebut", "asc")
                );
                const snap = await getDocs(qEvts);
                const list = [];
                snap.forEach((d) => {
                    const data = d.data();
                    // garde le strict nécessaire pour l’UI
                    list.push({ id: d.id, title: data?.title, dateDebut: data?.dateDebut, cover: data?.cover });
                });
                setEvents(list);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        }
    }, [events.length, orgID]);

    const isEmptyFilters = useMemo(() => {
        const f = filters;
        return (!f.filterEvent?.length) &&
            (!f.filterInterest?.length) &&
            (!f.filterNbEvent?.min && !f.filterNbEvent?.max) &&
            (!f.filterBillet?.min && !f.filterBillet?.max) &&
            (!f.totalDepense?.min && !f.totalDepense?.max) &&
            (!f.lastDateOrder?.after && !f.lastDateOrder?.before) &&
            (f.filterNewsletter == null) &&
            (f.filterNotif == null) &&
            (f.filterAbonne == null) &&
            (!f.filterHist);
    }, [filters]);

    /** ----- Compteur approx (unique) ----- */
    const { count, loading: countLoading, error: countError } = useApproxContactsCount(
        endpointApproxCount,
        orgID,
        filters,
        { debounceMs: 450 }
    );

    /** ----- Actions Cloud Functions ----- */
    const createDynamiquePopulation = useCallback(async () => {
        const payload = {
            orgID,
            filters,
            emoji: chosenEmoji,
            popName: nom,
            type,
            desc: desc || null
        };
        const resp = await fetch(endpointCreatePop, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });
        if (!resp.ok) throw new Error(`Erreur HTTP ${resp.status}`);
        const json = await resp.json();
        return json?.totalUsers ?? 0;
    }, [orgID, filters, chosenEmoji, nom, type, desc]);

    const uploadCsvToPopulationClientBatches = useCallback(async (popId, csvData, headers, fieldMapping) => {
        const payload = {
            orgID,
            popId,                // population cible
            headers,              // ex: ["email","nom","prenoms","telephone","abonne"]
            fieldMapping,         // ex: { email:"email", nom:"nom", prenoms:"prenoms", telephone:"telephone", abonne:"abonne" }
            rows: csvData,        // tes lignes parsées via Papa.parse
            enforceDailyLimit: true,
            updatePopulationMeta: true,
        };
        const resp = await fetch(endpointImportCSV, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });
        if (!resp.ok) throw new Error(`Erreur HTTP ${resp.status}`);
        const json = await resp.json();
        return json?.totalUsers ?? 0;
    }, [orgID]);

    const createPopulation = useCallback(async () => {
        setLoading(true);
        try {
            if (type === 0) {
                await createDynamiquePopulation();
            } else {
                // Création du doc de population manuelle (vide) → l’import CSV remplira /users
                const popRef = doc(collection(db, `organisations/${orgID}/crm/${orgID}/populations`));
                await setDoc(popRef, {
                    emoji: chosenEmoji,
                    name: nom,
                    type,
                    size: 0,
                    filters: null,
                    description: desc || null,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
                // Lance l’upload CSV côté client (fallback si pas de CF d’import)
                if (csvData.length) {
                    await uploadCsvToPopulationClientBatches(popRef.id, csvData, headers, fieldMapping);
                }
            }
            // fermeture du drawer gérée depuis ton parent (garde ton setOpenDrawer(false) si nécessaire)
            setOpenDrawer(false);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [type, createDynamiquePopulation, orgID, chosenEmoji, nom, desc, csvData, headers, fieldMapping]);

    const savePopulation = useCallback(async () => {
        setLoading(true);
        try {
            if (!populationID) return;
            const popRef = doc(db, `organisations/${orgID}/crm/${orgID}/populations`, populationID);
            await setDoc(popRef, {
                emoji: chosenEmoji,
                name: nom,
                description: desc || null,
                filters: type === 0 ? filters : null,
                updatedAt: new Date()
            }, { merge: true });

            if (type === 1 && csvData.length) {
                await uploadCsvToPopulationClientBatches(populationID, csvData, headers, fieldMapping);
            }
            setOpenDrawer(false);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [populationID, orgID, chosenEmoji, nom, desc, type, filters, csvData, headers, fieldMapping]);

    /** ----- Upload CSV : parsing + mapping ----- */
    const handleFileUpload = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setLoading(true);
        setFileName(file.name);
        // Abort si un parse en cours
        if (csvAbortRef.current) csvAbortRef.current();

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            worker: true, // Thread séparé
            dynamicTyping: true,
            // beforeFirstChunk: () => {},
            complete: (result) => {
                const { data, meta, errors } = result;
                if (errors?.length) {
                    console.error("CSV parse errors:", errors.slice(0, 3));
                }
                const cols = meta.fields || [];
                setHeaders(cols);

                // mapping auto par défaut
                const initialMapping = {};
                cols.forEach((h) => { initialMapping[h] = DEFAULT_MAPPINGS[h?.toLowerCase()] || ""; });
                setFieldMapping(initialMapping);
                setCsvData(data);
                setLoading(false);
            },
            error: (err, file, inputElem, reason) => {
                console.error("CSV parse error:", err, reason);
                setLoading(false);
            },
            // step: undefined,
            chunkSize: 1024 * 1024
        });
    }, []);

    const handleFieldMappingChange = useCallback((csvHeader, targetField) => {
        setFieldMapping((prev) => ({ ...prev, [csvHeader]: targetField }));
    }, []);

    const handleRemoveFile = useCallback(() => {
        setCsvData([]);
        setFileName(null);
        setHeaders([]);
        setFieldMapping({});
    }, []);

    /** ----- Rendu ----- */
    return (
        <Box>
            {!isEdit && step === 0 && (
                <Box pb={3} px={3} sx={{ height: { xs: "75vh", sm: "78vh", xl: "85vh" }, overflow: "auto" }}>
                    <Stack spacing={3}>
                        <Box p={2} sx={{ bgcolor: "#f2f2f2", borderRadius: 1 }}>
                            <Typography variant="body2">
                                Une population est un groupe de contacts partageant des points communs ou comportements. Un contact peut appartenir à plusieurs populations.
                            </Typography>
                        </Box>

                        <Typography variant="subtitle1">Sélectionnez un type de population</Typography>

                        <Box p={2} sx={{ border: "2px solid #f2f2f2", borderRadius: 1 }}>
                            <Stack spacing={2} alignItems="start">
                                <Typography variant="subtitle2">Population dynamique</Typography>
                                <Typography variant="body2">
                                    Définissez des conditions. Les contacts seront automatiquement ajoutés/retirés selon les filtres.
                                </Typography>
                                <CustomButton
                                    type="primaire"
                                    buttonText="Créer une population dynamique"
                                    onClick={() => { setType(0); setStep(1); }}
                                />
                            </Stack>
                        </Box>

                        <Box p={2} sx={{ border: "2px solid #f2f2f2", borderRadius: 1 }}>
                            <Stack spacing={2} alignItems="start">
                                <Typography variant="subtitle2">Population manuelle via CSV</Typography>
                                <Typography variant="body2">Importez une liste de contacts depuis un fichier .csv</Typography>
                                <CustomButton
                                    type="primaire"
                                    buttonText="Créer une population manuelle"
                                    onClick={() => { setType(1); setStep(1); }}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            )}

            {step === 1 && type === 0 && !isDynamicEdit && (
                <PopulationFilters
                    value={filters}
                    onChange={onChangeFilters}
                    events={events}
                    types={types}
                    title="Filtrer les contacts"
                    onAccordionChange={handleExpandPanel1}
                    sx={{ height: { xs: "82vh", sm: "80vh", md: "80vh", xl: "82vh", lg: "85vh" } }}
                />
            )}

            {step === 1 && type === 1 && (
                <Box pb={3} px={3} sx={{ height: { xs: "84vh", sm: "80.25vh", xl: "84.25vh" }, overflow: "auto" }}>
                    <Typography variant="h5">Importer des utilisateurs</Typography>

                    <Accordion expanded={expanded === "panel1"} onChange={handleExpandPanel1} sx={{ bgcolor: "#F0F0F0", mb: 2, mt: 3 }}>
                        <AccordionSummary expandIcon={<Iconify icon="ep:arrow-down-bold" />}>
                            <Stack>
                                <Stack spacing={1} direction="row" alignItems="center">
                                    <Iconify icon="mdi:calendar-outline" sx={{ width: 20, height: 20 }} />
                                    <Typography variant="body2"><strong>0/2000</strong> contacts importés aujourd&apos;hui</Typography>
                                </Stack>
                                <Typography textAlign="center" variant="body2">
                                    {csvData.length > 0 ? `${nf(csvData.length)} contacts prêts à être ajoutés` : ""}
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="caption">
                                Pour des raisons de performance/coût, l&apos;import est limité à 2000 par jour.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    {fileName ? (
                        <Stack spacing={2}>
                            <Stack sx={{ border: "2px solid #f2f2f2", bgcolor: "#f2f2f2", borderRadius: 1, p: 2 }}
                                direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="body2">{fileName}</Typography>
                                <IconButton onClick={handleRemoveFile}><Iconify icon="material-symbols-light:delete-outline" /></IconButton>
                            </Stack>

                            <Stack sx={{ border: "2px solid #f2f2f2", borderRadius: 1, p: 3 }} spacing={2}>
                                <Stack>
                                    <Typography variant="subtitle1">Correspondance des informations</Typography>
                                    <Typography variant="caption">Sélectionnez la colonne CSV pour chaque champ cible</Typography>
                                </Stack>

                                {headers.map((header) => (
                                    <Stack key={header} spacing={1}>
                                        <Typography variant="subtitle2">{header}</Typography>
                                        <Select
                                            value={fieldMapping[header] ?? ""}
                                            onChange={(e) => handleFieldMappingChange(header, e.target.value)}
                                            size="small"
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            <MenuItem value="email">Email</MenuItem>
                                            <MenuItem value="telephone">Téléphone</MenuItem>
                                            <MenuItem value="nom">Nom</MenuItem>
                                            <MenuItem value="prenoms">Prénom</MenuItem>
                                            <MenuItem value="abonne">Abonné</MenuItem>
                                        </Select>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack spacing={1}>
                            {populationID && <Typography variant="subtitle2">Importer des contacts supplémentaires dans ce segment</Typography>}
                            <Button
                                fullWidth size="small" variant="contained" component="label"
                                sx={{
                                    mt: 2, textTransform: "initial", boxShadow: "none",
                                    border: "1px dashed gainsboro", color: "black", bgcolor: "white",
                                    "&:hover": { bgcolor: "white", color: "#5E17EB", borderColor: "#5E17EB" }
                                }}
                            >
                                <input hidden accept=".csv" id="select-image" type="file" onChange={handleFileUpload} />
                                <Stack p={2} alignItems="center" justifyContent="space-around">
                                    {loading ? "Chargement…" : (<>
                                        <LucideIcon name="Upload" />
                                        <Typography variant="body2">Fichier CSV</Typography>
                                        <Typography variant="body2">Déposez votre fichier ici</Typography>
                                    </>)}
                                </Stack>
                            </Button>
                            <Typography pt={2} variant="body2">
                                Votre CSV doit au minimum contenir une colonne <strong>Email</strong>.
                                Pour l’envoi de newsletters, ajoutez une colonne <strong>Abonné</strong> (yes/no).
                            </Typography>
                            <ImportContactTemplateButton />
                        </Stack>
                    )}
                </Box>
            )}

            {step === 2 && (
                <Box pb={3} px={3} sx={{ height: { xs: "75vh", sm: "78vh", xl: "85vh" }, overflow: "auto" }}>
                    <Typography variant="h5" py={1}>
                        {isEdit ? "Modifier la population" : "Nouvelle population 2/2"}
                    </Typography>

                    {isDynamicEdit && (
                        <Box p={2} sx={{ bgcolor: "#FFF7E6", border: "1px solid #FFE0B2", borderRadius: '7px', mb: 2 }}>
                            <Typography variant="body2">
                                Cette population est <strong>dynamique</strong>. Vous pouvez modifier l’<strong>emoji</strong>, le <strong>nom</strong> et la <strong>description</strong>.
                                Les <strong>filtres</strong> ne sont pas modifiables ici.
                            </Typography>
                        </Box>
                    )}

                    <Stack spacing={1} pt={1}>
                        <Typography variant="subtitle2">Emoji</Typography>
                        <Box onClick={onTogglePicker}
                            sx={{
                                bgcolor: "#F5EFFA", borderRadius: '7px', width: 80, height: 80, border: "1px solid gainsboro",
                                fontSize: "3rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                            {chosenEmoji}
                        </Box>
                        <Suspense fallback={<Typography variant="caption">Chargement du sélecteur…</Typography>}>
                            {showPicker && <EmojiPicker onEmojiClick={onEmojiClick} />}
                        </Suspense>
                    </Stack>

                    <Stack spacing={1} pt={2}>
                        <Typography variant="subtitle2">Nom</Typography>
                        <TextField size="small" value={nom} onChange={(e) => setNom(e.target.value)} />
                    </Stack>

                    <Stack spacing={1} py={2}>
                        <Typography variant="subtitle2">Description</Typography>
                        <TextField value={desc} onChange={(e) => setDesc(e.target.value)} multiline minRows={2} />
                    </Stack>
                    {(isDynamicEdit || type === 0) && <FilterChipsSummary filters={filters} events={events} types={types} />}


                    {type === 0 && (
                        <Stack spacing={2}>
                            <Typography variant="subtitle2">Contacts correspondants</Typography>
                            <Typography variant="caption">
                                Segment dynamique : les contacts seront ajoutés/retirés automatiquement selon les conditions.
                            </Typography>
                        </Stack>
                    )}
                </Box>
            )}

            {/* Footer actions */}
            {step !== 0 && (
                <Stack direction="column" spacing={1} p={1} alignItems="end" sx={{ borderTop: "1px solid gainsboro" }}>
                    {/* Ne montre pas l'estimation pour l'édition dynamique */}
                    {type === 0 && !isDynamicEdit && (
                        <Typography variant="body2" sx={{ mr: 1 }}>
                            {countLoading ? "Calcul…" : (count != null ? `≈ ${new Intl.NumberFormat("fr-FR").format(count)} contacts` : "")}
                        </Typography>
                    )}

                    <Stack direction="row" spacing={2}>
                        <CustomButton
                            type="secondaire"
                            buttonText={step === 1 ? "Annuler" : "Retour"}
                            onClick={() => {
                                if (isDynamicEdit) {
                                    // pas de step 1 pour l’édition dynamique — ferme ou remonte d’un cran
                                    setOpenDrawer(false); // si tu gères un drawer
                                } else {
                                    if ((step - 1) === 0) {
                                        setOpenDrawer(false);
                                    }
                                    setStep((s) => Math.max(0, s - 1));
                                }
                            }}
                        />
                        <CustomButton
                            type="primaire"
                            loading={loading || (type === 0 && !isDynamicEdit && countLoading)}
                            disabled={(type === 0 && !isDynamicEdit && isEmptyFilters)}
                            buttonText={
                                isDynamicEdit
                                    ? "Sauvegarder"
                                    : step === 2
                                        ? (populationID ? "Sauvegarder" : "Créer la population")
                                        : "Suivant"
                            }
                            onClick={async () => {
                                if (isDynamicEdit) {
                                    await savePopulation(); // ne mettra à jour que emoji/nom/description
                                    return;
                                }
                                if (step === 2) {
                                    if (populationID) await savePopulation(); else await createPopulation();
                                } else {
                                    setStep(step + 1);
                                }
                            }}
                        />
                    </Stack>
                </Stack>
            )}

        </Box>
    );
}